import { login } from '../auth.js';
import { t } from '../i18n.js';
import * as router from '../router.js';
import { AUTH_NOTICE_STORAGE_KEY } from '../router.js';
import { toast } from '../components/toast.js';
import { ApiError } from '../api.js';
import { mountAuthedShell } from '../shell.js';
import { STATUS_TINT_CLASS } from '../components/status-pill.js';

export const route = {
  public: true,
  async mount(target) {
    target.classList.remove('p-4', 'sm:p-6');

    const wrap = document.createElement('div');
    wrap.className = 'min-h-[calc(100vh-4rem)] flex items-center justify-center p-4 sm:p-6';

    const card = document.createElement('div');
    card.className = 'card w-full max-w-sm space-y-4';

    const h = document.createElement('h1');
    h.textContent = t('login.title');
    card.append(h);

    // Why the user landed here (session expired / revoked) — stored by
    // router.handleUnauthorized just before the redirect.
    let noticeKey = null;
    try {
      noticeKey = sessionStorage.getItem(AUTH_NOTICE_STORAGE_KEY);
      if (noticeKey) sessionStorage.removeItem(AUTH_NOTICE_STORAGE_KEY);
    } catch { /* private mode */ }
    if (noticeKey) {
      const notice = document.createElement('p');
      notice.setAttribute('data-role', 'auth-notice');
      notice.setAttribute('role', 'status');
      notice.className = `rounded-xl px-3 py-2 text-sm ${STATUS_TINT_CLASS.warning}`;
      notice.textContent = t(noticeKey);
      card.append(notice);
    }

    const form = document.createElement('form');
    form.className = 'space-y-3';
    form.noValidate = true;

    const mkField = (id, type, label, autocomplete) => {
      const w = document.createElement('div');
      const l = document.createElement('label');
      l.className = 'label';
      l.htmlFor = id;
      l.textContent = label;
      w.append(l);
      const inp = document.createElement('input');
      inp.className = 'input';
      inp.id = id;
      inp.name = id;
      inp.type = type;
      inp.autocomplete = autocomplete;
      inp.required = true;
      w.append(inp);
      return { w, inp };
    };

    const { w: emailW, inp: emailInp } = mkField('email', 'email', t('login.email'), 'username');
    const { w: pwW, inp: pwInp } = mkField('password', 'password', t('login.password'), 'current-password');
    form.append(emailW);
    form.append(pwW);

    const rememberWrap = document.createElement('label');
    rememberWrap.className = 'flex items-center gap-3 cursor-pointer select-none';
    rememberWrap.htmlFor = 'remember_me';
    const rememberCb = document.createElement('input');
    rememberCb.type = 'checkbox';
    rememberCb.id = 'remember_me';
    rememberCb.name = 'remember_me';
    rememberCb.className = 'h-5 w-5 rounded border-border dark:border-border-dark text-primary-600 focus:ring-primary-600';
    rememberCb.checked = false;
    const rememberSpan = document.createElement('span');
    rememberSpan.className = 'text-sm';
    rememberSpan.textContent = t('auth.rememberMe');
    rememberWrap.append(rememberCb, rememberSpan);
    form.append(rememberWrap);

    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'btn-primary w-full';
    submit.textContent = t('login.submit');
    form.append(submit);

    let pending = false;
    form.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      if (pending) return;
      const email = emailInp.value.trim();
      const pw = pwInp.value;
      function markErr(el, hasError) {
        el.classList.toggle('border-danger', hasError);
        el.classList.toggle('focus:ring-danger/60', hasError);
      }
      const missingEmail = !email;
      const missingPw = !pw;
      markErr(emailInp, missingEmail);
      markErr(pwInp, missingPw);
      if (missingEmail || missingPw) {
        toast.show(missingEmail ? t('errors.email_required') : t('errors.password_required'), 'warning');
        (missingEmail ? emailInp : pwInp).focus();
        return;
      }
      // Clear marks once the user starts retyping.
      emailInp.addEventListener('input', () => markErr(emailInp, false), { once: true });
      pwInp.addEventListener('input', () => markErr(pwInp, false), { once: true });
      pending = true;
      submit.disabled = true;
      const originalText = submit.textContent;
      submit.textContent = '…';
      try {
        await login(email, pw, rememberCb.checked);
        await mountAuthedShell();
        router.go('/');
      } catch (e) {
        if (e instanceof ApiError) {
          if (e.code === 'unauthorized') {
            toast.show(t('login.error_invalid'), 'error');
          } else if (e.code === 'network') {
            toast.show(t('errors.network'), 'error');
          } else {
            toast.show(e.message || t('errors.server'), 'error');
          }
        } else {
          toast.show(t('errors.server'), 'error');
        }
      } finally {
        pending = false;
        submit.disabled = false;
        submit.textContent = originalText;
      }
    });

    card.append(form);
    wrap.append(card);
    target.append(wrap);

    emailInp.focus();

    return () => {
      target.classList.add('p-4', 'sm:p-6');
    };
  },
};
